/* eslint-disable @typescript-eslint/member-ordering */
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { CallState } from '@lcs/shared/api';
import { Actions, ofType } from '@ngrx/effects';
import { LocationInput } from 'generated/graphql-types';
import { Countries } from 'src/app/common-components/confirmation-dialog/enumerations/countries';
import { UsStates } from 'src/app/common-components/confirmation-dialog/enumerations/states';
import { DetailView } from 'src/app/common-components/detail/detail-view';
import { LocationDetailSaveEvent } from '../models/location-detail-form-submit-event';
import { LocationDetailView } from '../models/location-detail-view.model';
import { LocationPrivileges } from '../models/location-privileges';
import { LocationDetailFormService } from '../services/location-detail-form.service';
import * as LocationDetailActions from '../state/location-detail.actions';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-location-detail-view',
    styleUrls: ['./location-detail-view.component.scss'],
    templateUrl: './location-detail-view.component.html',
    standalone: false
})
export class LocationDetailViewComponent extends DetailView {
   countries = Object.values(Countries);
   states = UsStates.states;

   @Input() get location(): LocationDetailView | null {
      return this._location;
   }

   set location(value: LocationDetailView | null) {
      this._location = value;
      if (value) {
         this.formService.updateLocation(value);
      }
   }

   @Input() set locationPrivileges(privileges: LocationPrivileges) {
      this._locationsPrivileges = privileges;
      if (!privileges?.canEdit) {
         this.form.disable();
      }
   }

   get locationPrivileges(): LocationPrivileges {
      return this._locationsPrivileges;
   }

   @Input() callState: CallState;

   @Input() isSystemUser: boolean;

   @Output() formSaveCanceled = new EventEmitter<void>();

   @Output() formSubmitted = new EventEmitter<LocationDetailSaveEvent>();

   @Output() deleteLocation = new EventEmitter<{ locationId: number }>();

   form: UntypedFormGroup;

   private _location: LocationDetailView | null;

   private _locationsPrivileges: LocationPrivileges;

   constructor(public formService: LocationDetailFormService, private actions$: Actions) {
      super();
      this.form = this.formService.form;
   }

   loadSuccess$ = this.actions$.pipe(ofType(LocationDetailActions.loadLocationDetailSuccess));

   fileNotFound = false;

   onFileNotFound() {
      this.fileNotFound = true;
   }

   onSubmit(): void {
      if (this.form.invalid) {
         return;
      }

      const submitEvent = this.getFormSubmittedEvent();
      if (submitEvent) {
         this.formSubmitted.emit(submitEvent);
      }
   }

   onCancel(): void {
      this.formSaveCanceled.emit();
   }

   // locationDisplayWith(option: LocationTypes): string {
   //    return `${option ?? ''}`;
   // }

   onDeleteClick(): void {
      if (this.location) {
         this.deleteLocation.emit({
            locationId: this.location.locationId,
         });
      }
   }

   private getFormSubmittedEvent(): LocationDetailSaveEvent | null {
      if (!this.formService.form.dirty || !this.location) {
         return null;
      }

      const location = this.formService.getValue(this.location.locationId);
      let locationFields: (keyof LocationInput)[] = [];
      locationFields = this.formService.addFieldForDirtyControl(locationFields, 'account', 'accountId');
      locationFields = this.formService.addFieldForDirtyControl(locationFields, 'name');
      locationFields = this.formService.addFieldForDirtyControl(locationFields, 'locationId');
      locationFields = this.formService.addFieldForDirtyControl(locationFields, 'houseNumber');
      locationFields = this.formService.addFieldForDirtyControl(locationFields, 'directionalPrefix');
      locationFields = this.formService.addFieldForDirtyControl(locationFields, 'streetName');
      locationFields = this.formService.addFieldForDirtyControl(locationFields, 'streetNameSuffix');
      locationFields = this.formService.addFieldForDirtyControl(locationFields, 'directionalPostfix');
      locationFields = this.formService.addFieldForDirtyControl(locationFields, 'city');
      locationFields = this.formService.addFieldForDirtyControl(locationFields, 'state');
      locationFields = this.formService.addFieldForDirtyControl(locationFields, 'postalCode');
      locationFields = this.formService.addFieldForDirtyControl(locationFields, 'comment');
      locationFields = this.formService.addFieldForDirtyControl(locationFields, 'country');
      locationFields = this.formService.addFieldForDirtyControl(locationFields, 'timeZone', 'timeZoneId');
      locationFields = this.formService.addFieldForDirtyControl(locationFields, 'isE911Validated');
      locationFields = this.formService.addFieldForDirtyControl(locationFields, 'mostRecentE911ValidationResponse');

      return { location, locationFields };
   }

   handleStateChange(isDirty: boolean): void {
      if (isDirty) {
         this.form.controls['state'].markAsDirty();
         this.form.controls['state'].updateValueAndValidity();
      }
   }

   countryDisplayWith(option: Countries): string {
      return `${option ?? ''}`;
   }

   countryTrackBy = (option: Countries) => option;

   stateDisplayWith(option: { abbreviation: string; name: string }): string {
      return `${option ?? ''}`;
   }

   countrySearch(searchText: string) {
      this.countries = Object.values(Countries).filter((country) =>
         country.toUpperCase().includes(searchText.toUpperCase())
      );
   }

   countrySearchReset() {
      this.countries = Object.values(Countries);
   }

   stateSearch(searchText: string) {
      this.states = UsStates.states.filter(
         (state) =>
            state.name.toUpperCase().includes(searchText.toUpperCase()) ||
            state.abbreviation.toUpperCase().includes(searchText.toUpperCase())
      );
   }

   stateSearchReset() {
      this.states = UsStates.states;
   }
}
